import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, User, LogIn } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/products', label: 'Products' },
    { path: '/branches', label: 'Branches' },
    { path: '/blog', label: 'Health Tips' },
    { path: '/prescription', label: 'Upload Prescription' },
    { path: '/careers', label: 'Careers' },
    { path: '/faqs', label: 'FAQs' },
    { path: '/contact', label: 'Contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      {/* Top bar with 24/7 badge */}
      <div className="bg-orange-600 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1">
              <Phone className="w-3 h-3" />
              0539543737
            </span>
            <span className="hidden md:flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              Ashiyie, Accra
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="font-semibold">OPEN 24 HOURS</span>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_189a4100-90ea-4234-8592-108cc00e468b/artifacts/q72i6epx_IMG_8643.jpeg" 
              alt="City Central Pharmacy" 
              className="h-12 sm:h-14 md:h-16 lg:h-[72px] w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors hover:text-orange-600 ${
                  isActive(link.path) ? 'text-orange-600 border-b-2 border-orange-600' : 'text-gray-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <Link to="/portal/dashboard">
                <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                  <User className="mr-2 w-4 h-4" />
                  My Account
                </Button>
              </Link>
            ) : (
              <Link to="/login">
                <Button variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                  <LogIn className="mr-2 w-4 h-4" />
                  Login
                </Button>
              </Link>
            )}
            <Link to="/order-medicines">
              <Button className="bg-orange-600 hover:bg-orange-700 text-white">
                Order Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <nav className="lg:hidden py-4 border-t bg-white">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 px-4 text-sm font-medium transition-colors ${
                  isActive(link.path) ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="px-4">
              {isAuthenticated ? (
                <Link to="/portal/dashboard" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="outline" className="w-full mt-4 border-orange-600 text-orange-600">
                    <User className="mr-2 w-4 h-4" />
                    My Account
                  </Button>
                </Link>
              ) : (
                <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                  <Button variant="outline" className="w-full mt-4 border-orange-600 text-orange-600">
                    <LogIn className="mr-2 w-4 h-4" />
                    Login / Register
                  </Button>
                </Link>
              )}
              <Link to="/order-medicines" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full mt-2 bg-orange-600 hover:bg-orange-700 text-white">
                  Order Now
                </Button>
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};